import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Modal } from '@cortex/web-design-system/components/ui/Modal';
import { SortableHeader, type SortDir } from '../KpiDetailModal/SortableHeader.js';
import { compareTableValues } from '../../../lib/table-sort.js';
import { useKpiBugBreakdownDetails } from '../../../hooks/useKpi.js';
import type {
  BugBreakdownCategory,
  BugBreakdownDetail,
  BugTrendUnit,
  KpiTeam,
} from '../../../types/api.js';

/**
 * カテゴリの表示順（重要度順、チャートの凡例と同じ並び）とチップ色。
 * チャートでは preRelease / notBug / duplicate を「その他」に合算しているが、
 * ドリルダウンでは分類されたことが分かるよう個別カテゴリで表示する。
 * @graph-connects none
 */
const CATEGORY_META: { key: BugBreakdownCategory; color: string }[] = [
  { key: 'devQuality', color: 'var(--color-accent-primary)' },
  { key: 'kpiEscaped', color: '#818cf8' },
  { key: 'latent', color: 'var(--color-error)' },
  { key: 'specGap', color: 'var(--color-warning)' },
  { key: 'business', color: 'var(--color-text-muted)' },
  { key: 'autoDetected', color: '#14b8a6' },
  { key: 'preRelease', color: '#a78bfa' },
  { key: 'notBug', color: '#94a3b8' },
  { key: 'duplicate', color: '#cbd5e1' },
  { key: 'other', color: '#64748b' },
];

/** カテゴリ→重要度順 index（並べ替え用） @graph-connects none */
const CATEGORY_RANK = new Map(CATEGORY_META.map((meta, index) => [meta.key, index]));

/** ソート可能な列キー @graph-connects none */
type BreakdownSortKey =
  | 'category'
  | 'issueKey'
  | 'summary'
  | 'incidentLevel'
  | 'assignee'
  | 'createdDate';

/**
 * ドリルダウン対象の期間。チャートでクリックされたバーの集計単位と期間キーのペア
 * （unit=month のとき YYYY-MM、unit=week のとき週開始日・月曜 YYYY-MM-DD）
 */
export interface BugBreakdownDetailPeriod {
  /** 集計単位（月次/週次・月曜始まり） */
  unit: BugTrendUnit;
  /** 期間キー（月次: YYYY-MM / 週次: 週開始日 YYYY-MM-DD） */
  period: string;
}

export interface BugBreakdownDetailModalProps {
  /** モーダル表示状態 */
  open: boolean;
  /** 閉じるコールバック */
  onClose: () => void;
  /** 対象期間（単位つき）。未選択時は null */
  period: BugBreakdownDetailPeriod | null;
  /** チーム絞り込み（チャートと同じ絞り込みで一覧を出す）。undefined / all は全体 */
  team?: KpiTeam;
}

/**
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business 信頼性ページのチャート期間クリック→期間別バグ詳細ドリルダウンモーダル。指定起票月（または起票週・月曜始まり）の案件起因＋潜在バグ個別課題を重要度順カテゴリ付きで一覧表示し、Backlogリンク・インシデントレベル・担当者を確認できる
 * @graph-connects useKpiBugBreakdownDetails [calls] 指定期間のバグ個別課題一覧を取得
 * @graph-connects Modal [calls] 汎用モーダルで内容を表示
 */
export function BugBreakdownDetailModal({
  open,
  onClose,
  period,
  team,
}: BugBreakdownDetailModalProps) {
  const { t } = useTranslation();
  const [sortKey, setSortKey] = useState<BreakdownSortKey | null>(null);
  const [sortDir, setSortDir] = useState<SortDir>('asc');

  const params = useMemo(() => {
    if (!open || period === null) return null;
    // 全体表示（undefined / all）のときは team を付けず、チャート側と同じクエリキーに揃える
    const teamParam = team !== undefined && team !== 'all' ? { team } : {};
    return period.unit === 'week'
      ? { week: period.period, ...teamParam }
      : { month: period.period, ...teamParam };
  }, [open, period, team]);

  const { data, isLoading } = useKpiBugBreakdownDetails(params);

  const sortedBugs = useMemo<BugBreakdownDetail[]>(() => {
    if (!data?.data) return [];
    // 未ソート時の既定: 重要度順（チャート凡例と同じ）→ 起票日順
    if (sortKey === null) {
      return [...data.data].sort((a, b) => {
        const rankDiff =
          (CATEGORY_RANK.get(a.category) ?? CATEGORY_META.length) -
          (CATEGORY_RANK.get(b.category) ?? CATEGORY_META.length);
        if (rankDiff !== 0) return rankDiff;
        return a.createdDate.localeCompare(b.createdDate);
      });
    }
    // カテゴリ列は文字列ではなく重要度順 index で比較する
    const valueOf = (bug: BugBreakdownDetail) =>
      sortKey === 'category'
        ? (CATEGORY_RANK.get(bug.category) ?? CATEGORY_META.length)
        : bug[sortKey];
    return [...data.data].sort((a, b) => compareTableValues(valueOf(a), valueOf(b), sortDir));
  }, [data, sortKey, sortDir]);

  const handleSort = (key: BreakdownSortKey) => {
    if (sortKey === key) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDir('asc');
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      size="full"
      title={
        period?.unit === 'week'
          ? t('reliability.detail.titleWeekly', { week: period.period })
          : t('reliability.detail.title', { month: period?.period ?? '' })
      }
      description={t(
        period?.unit === 'week'
          ? 'reliability.detail.descriptionWeekly'
          : 'reliability.detail.description',
        { count: sortedBugs.length },
      )}
    >
      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <div className="loading-spinner" />
        </div>
      ) : sortedBugs.length === 0 ? (
        <div className="text-center py-12 text-[var(--color-text-muted)]">
          {t(
            period?.unit === 'week'
              ? 'reliability.detail.noItemsWeekly'
              : 'reliability.detail.noItems',
          )}
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[900px]">
            <thead>
              <tr className="border-b border-[var(--color-border)]">
                <th className="text-left py-2 px-2 font-medium text-[var(--color-text-muted)] whitespace-nowrap w-[110px]">
                  <SortableHeader
                    label={t('reliability.detail.createdDate')}
                    sortKey="createdDate"
                    currentKey={sortKey}
                    currentDir={sortDir}
                    onSort={handleSort}
                  />
                </th>
                <th className="text-left py-2 px-2 font-medium text-[var(--color-text-muted)] whitespace-nowrap w-[200px]">
                  <SortableHeader
                    label={t('reliability.detail.category')}
                    sortKey="category"
                    currentKey={sortKey}
                    currentDir={sortDir}
                    onSort={handleSort}
                  />
                </th>
                <th className="text-left py-2 px-2 font-medium text-[var(--color-text-muted)] whitespace-nowrap w-[150px]">
                  <SortableHeader
                    label={t('reliability.detail.issueKey')}
                    sortKey="issueKey"
                    currentKey={sortKey}
                    currentDir={sortDir}
                    onSort={handleSort}
                  />
                </th>
                <th className="text-left py-2 px-2 font-medium text-[var(--color-text-muted)] min-w-[280px]">
                  <SortableHeader
                    label={t('reliability.detail.summary')}
                    sortKey="summary"
                    currentKey={sortKey}
                    currentDir={sortDir}
                    onSort={handleSort}
                  />
                </th>
                <th className="text-left py-2 px-2 font-medium text-[var(--color-text-muted)] whitespace-nowrap w-[150px]">
                  <SortableHeader
                    label={t('reliability.detail.incidentLevel')}
                    sortKey="incidentLevel"
                    currentKey={sortKey}
                    currentDir={sortDir}
                    onSort={handleSort}
                  />
                </th>
                <th className="text-left py-2 px-2 font-medium text-[var(--color-text-muted)] whitespace-nowrap w-[120px]">
                  <SortableHeader
                    label={t('reliability.detail.assignee')}
                    sortKey="assignee"
                    currentKey={sortKey}
                    currentDir={sortDir}
                    onSort={handleSort}
                  />
                </th>
              </tr>
            </thead>
            <tbody>
              {sortedBugs.map((bug) => (
                <tr
                  key={bug.issueKey}
                  className="border-b border-[var(--color-border)] last:border-b-0 hover:bg-[var(--color-bg-hover)]"
                >
                  <td className="py-2 px-2 text-[var(--color-text-secondary)] whitespace-nowrap">
                    {bug.createdDate}
                  </td>
                  <td className="py-2 px-2 whitespace-nowrap">
                    <span className="inline-flex items-center gap-1.5">
                      <span
                        aria-hidden="true"
                        className="inline-block w-2.5 h-2.5 rounded-sm"
                        style={{
                          backgroundColor:
                            CATEGORY_META.find((meta) => meta.key === bug.category)?.color ??
                            '#64748b',
                        }}
                      />
                      <span className="text-[var(--color-text-primary)]">
                        {t(`reliability.category.${bug.category}`)}
                      </span>
                    </span>
                  </td>
                  <td className="py-2 px-2 whitespace-nowrap">
                    <a
                      href={`https://air-closet.backlog.jp/view/${bug.issueKey}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-[var(--color-accent-primary)] hover:underline"
                    >
                      {bug.issueKey}
                    </a>
                  </td>
                  <td className="py-2 px-2 text-[var(--color-text-primary)]">
                    <div className="line-clamp-2">{bug.summary || '-'}</div>
                  </td>
                  <td className="py-2 px-2 text-[var(--color-text-secondary)] whitespace-nowrap">
                    {bug.incidentLevel ?? '-'}
                  </td>
                  <td className="py-2 px-2 text-[var(--color-text-secondary)] whitespace-nowrap">
                    {bug.assignee ?? '-'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Modal>
  );
}
